import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { councilApiClient } from '../../services/councilApiClient';

export interface InvitePeerDialogProps {
  sessionId: string;
  /** The turn the affordance was opened on — the Camp parents here (fork
   *  origin). NULL on trail-joins: the backend parents the joiner's camp at
   *  the trail tip at materialization time (shared spines P2). */
  fromTurnId: string | null;
  /** Shared spines P2 — invite onto this EXISTING trail (camp ⋯ menu). */
  inviteTrailId?: string | null;
  /** Controlled peer selection id (the Host owns it via NewChatPicker). '' = none chosen. */
  inviteeId: string;
  /** The peer-chooser UI injected by the Host (a NewChatPicker trigger). Kept as a slot
   *  so the Dialog stays free of the usePersonas → api.ts import.meta chain. */
  peerField: React.ReactNode;
  onClose: () => void;
  onProposed?: (proposalId: string) => void;
  /** C2.3 — reconsider mode: send reconsider=true (fold-back switchbacks to the
   *  origin turn) and swap the heading/CTA copy. */
  reconsider?: boolean;
}

/**
 * InvitePeerDialog (C2.1 sp4 T5) — the operator invite affordance. Picks a peer +
 * a task and enqueues a GATED propose_invite at the current turn (POST
 * council/propose-invite). The operator approves it in the proposal queue before
 * the peer spawns onto the spine. Prop-driven (candidates supplied by the host) so
 * it stays decoupled + testable.
 */
export const InvitePeerDialog: React.FC<InvitePeerDialogProps> = ({
  sessionId,
  fromTurnId,
  inviteeId,
  peerField,
  onClose,
  onProposed,
  reconsider = false,
  inviteTrailId = null,
}) => {
  const { darkMode } = useTheme();
  const [task, setTask] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = !!inviteeId && task.trim().length > 0 && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const res = await councilApiClient.proposeInvite(sessionId, {
        invitee_persona_id: inviteeId,
        task: task.trim(),
        ...(fromTurnId ? { from_turn_id: fromTurnId } : {}),
        ...(inviteTrailId ? { invite_trail_id: inviteTrailId } : {}),
        ...(reconsider ? { reconsider: true } : {}),
      });
      onProposed?.(res.id);
      onClose();
    } catch (e) {
      setError((e as Error).message ?? 'Failed to propose the invite');
      setBusy(false);
    }
  };

  const fieldCls = `w-full mb-3 rounded border px-2 py-1.5 text-sm ${
    darkMode ? 'bg-gray-900 border-gray-700 text-gray-100' : 'bg-white border-gray-300 text-gray-900'
  }`;

  return (
    <div
      data-testid="invite-peer-dialog"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[500] flex items-center justify-center bg-black/40 p-4"
    >
      <div
        className={`w-full max-w-md rounded-lg p-5 shadow-xl ${
          darkMode ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-900'
        }`}
      >
        <h2 className="text-lg font-semibold mb-1">
          {inviteTrailId ? 'Invite another companion onto this trail'
            : reconsider ? '↻ Reconsider from this turn' : 'Invite a peer onto the trail'}
        </h2>
        <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {inviteTrailId
            ? "They camp on this trail alongside its residents — a reply to the trail reaches everyone. You'll approve the invite before they spawn."
            : reconsider
            ? "Pick a companion to re-examine this earlier turn. Their work folds back as a co-equal continuation re-blazed from here — you'll approve it first."
            : "They join at this turn and take on the task you set. You'll approve the invite before they spawn."}
        </p>

        <label className="block text-xs uppercase tracking-wide mb-1 text-gray-500 dark:text-gray-400">
          Peer
        </label>
        {peerField}

        <label className="block text-xs uppercase tracking-wide mb-1 text-gray-500 dark:text-gray-400">
          Task
        </label>
        <textarea
          data-testid="invite-peer-task"
          value={task}
          onChange={(e) => setTask(e.target.value)}
          rows={3}
          placeholder="e.g. Stress-test the auth flow and name the top 3 gaps."
          className={fieldCls}
        />

        {error && (
          <p data-testid="invite-peer-error" className="text-xs text-red-600 dark:text-red-400 mb-2">
            {error}
          </p>
        )}

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            data-testid="invite-peer-cancel"
            onClick={onClose}
            className="text-sm px-3 py-1.5 rounded border border-gray-300 dark:border-gray-600
                       text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="invite-peer-submit"
            onClick={submit}
            disabled={!canSubmit}
            className={`text-sm px-3 py-1.5 rounded font-medium text-white
                        bg-emerald-500 hover:bg-emerald-600 dark:bg-emerald-600 dark:hover:bg-emerald-500
                        ${!canSubmit ? 'opacity-60 cursor-not-allowed' : ''}`}
          >
            {busy ? 'Proposing…' : reconsider ? 'Propose reconsider' : 'Propose invite'}
          </button>
        </div>
      </div>
    </div>
  );
};
